'use client'

import { useRef } from 'react'
import Link from 'next/link'
import type { ContentItem } from '@/lib/types'
import { MovieCard } from './MovieCard'
import { RowSkeleton } from './Skeletons'

interface MovieRowProps {
  title: string
  icon?: string
  items: ContentItem[]
  loading?: boolean
  seeAllHref?: string
  error?: boolean
}

export function MovieRow({ title, icon, items, loading, seeAllHref, error }: MovieRowProps) {
  const scroller = useRef<HTMLDivElement>(null)

  const scroll = (dir: -1 | 1) => {
    const el = scroller.current
    if (!el) return
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: 'smooth' })
  }

  return (
    <section className="py-4">
      <div className="container-page mb-3 flex items-end justify-between">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold text-white sm:text-2xl">
          {icon && <span aria-hidden>{icon}</span>}
          {title}
        </h2>
        <div className="flex items-center gap-2">
          {seeAllHref && (
            <Link
              href={seeAllHref}
              className="text-sm font-medium text-neutral-400 transition-colors hover:text-crimson-400"
            >
              Tümünü gör →
            </Link>
          )}
          <div className="hidden items-center gap-1.5 sm:flex">
            <button
              onClick={() => scroll(-1)}
              aria-label="Geri kaydır"
              className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/5 text-white/70 transition hover:bg-white/10 hover:text-white"
            >
              ‹
            </button>
            <button
              onClick={() => scroll(1)}
              aria-label="İleri kaydır"
              className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/5 text-white/70 transition hover:bg-white/10 hover:text-white"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <div className="container-page">
        {error ? (
          <p className="rounded-xl border border-white/10 bg-ink-800 p-6 text-sm text-neutral-400">
            İçerikleri getirirken bir sorun oluştu. Lütfen tekrar deneyin.
          </p>
        ) : loading ? (
          <RowSkeleton />
        ) : items.length === 0 ? (
          <p className="rounded-xl border border-white/10 bg-ink-800 p-6 text-sm text-neutral-400">
            Şu an bu bölümde içerik bulunamadı.
          </p>
        ) : (
          <div
            ref={scroller}
            className="no-scrollbar -mx-1 flex snap-x gap-3 overflow-x-auto scroll-smooth px-1 pb-2"
          >
            {items.map((item) => (
              <MovieCard
                key={`${item.type}-${item.id}`}
                item={item}
                className="w-[140px] shrink-0 snap-start sm:w-[160px] md:w-[180px]"
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
